import React, { useEffect, useState } from 'react';
import { AnimatePresence, Reorder, motion, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DELIVERABLE_STATUSES, DELIVERABLE_TYPES, VIDEO_FORMATS } from '../../types/portal';
import type { Deliverable } from '../../types/portal';
import { deliverableTypeIcon, isVersioned, linkFieldsFor, metaLabel, statusOptionLabel, waitingLabel } from '../../utils/deliverables';
import { VersionHistory } from './VersionHistory';
import { TranscriptUpload } from './TranscriptUpload';
import { TranscriptRevisions } from './TranscriptRevisions';
import { TranscriptComments } from './TranscriptComments';
import { isDriveFolder, toDrivePreviewUrl, toDriveThumbnailUrl } from '../../utils/drive';
import { fetchLatestTranscriptJob, isTranscriptJobStalled, requestTranscriptJob, transcriptJobLabel, type TranscriptJob } from '../../utils/transcriptJobs';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
interface DeliverableAdminProps {
  deliverable: Deliverable;
  /** Reports which row is currently lifted, or null once it is set down. */
  onLift: (id: string | null) => void;
}

/**
 * The same type mark the client Deliverables list and Files page show, but
 * outlined rather than filled — the staff admin surface is already `bg-ink`
 * (see PortalApp.tsx), so the client tile's solid ink fill would have no
 * contrast here. A quiet border and a dimmer version of the same light
 * gradient keep the language recognisable without a second design.
 *
 * 9 Sep 2026: now also shows the same real Drive poster frame the client
 * tile got in the Drive-thumbnail pass — this staff row was left on the
 * plain icon at the time, which is why it never picked that up. Same
 * icon-first, fade-in-on-load, quietly-fall-back-to-the-icon behavior as
 * `TypeMark` in `components/DeliverableGroups.tsx`; only the frame (outlined
 * vs filled) differs, to match this surface's dark background.
 */
function StaffTypeMark({
  type,
  driveUrl
}: {
  type: Deliverable['type'];
  driveUrl?: string;
}) {
  const Icon = deliverableTypeIcon(type);
  const [loaded, setLoaded] = useState(false);
  const thumbnailUrl = driveUrl ? toDriveThumbnailUrl(driveUrl) : '';
  return <span aria-hidden className="relative hidden h-9 w-14 shrink-0 items-center justify-center overflow-hidden border border-paper/25 sm:flex" data-id="element-842" data-mp="3awalqjjcro2r">
      <span className="absolute inset-0" style={{
      background: 'radial-gradient(120% 140% at 20% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0) 75%)'
    }} data-id="element-843" data-mp="2i8ohd92awfeu" />
      <Icon className="relative h-3.5 w-3.5 text-paper/50" strokeWidth={1.25} data-id="element-844" data-mp="2s7vtxu17o60w" />
      {thumbnailUrl && <img key={thumbnailUrl} src={thumbnailUrl} alt="" loading="lazy" onLoad={() => setLoaded(true)} onError={() => setLoaded(false)} className={['absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-out', loaded ? 'opacity-100' : 'opacity-0'].join(' ')} data-id="element-845" data-mp="2haebcbgde70" />}
    </span>;
}

/** "V3" → "V4". A blank version starts at V1; anything else is left alone. */
function nextVersion(current: string): string {
  const match = /^v(\d+)$/i.exec(current.trim());
  if (match) return `V${Number(match[1]) + 1}`;
  return current.trim() ? current.trim() : 'V1';
}

/** Only Video and Audio deliverables can be sent to Descript for a transcript. */
function transcriptSourceUrl(deliverable: Deliverable): string {
  if (deliverable.type === 'Video') return deliverable.driveUrl;
  if (deliverable.type === 'Audio') return deliverable.audioUrl;
  return '';
}
export function DeliverableAdmin({
  deliverable,
  onLift
}: DeliverableAdminProps) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const {
    activeProject,
    staffUser,
    updateDeliverable,
    removeDeliverable,
    duplicateDeliverable,
    publishVersion,
    resolveChangeRequest,
    uploadTranscript,
    notify,
    focusDeliverableId
  } = usePortal();

  /** 'details' shows the client feedback; 'edit' also shows the configuration. */
  const [panel, setPanel] = useState<'closed' | 'details' | 'edit'>(focusDeliverableId === deliverable.id ? 'details' : 'closed');
  const open = panel !== 'closed';
  const editingRow = panel === 'edit';
  const setOpen = (next: boolean) => setPanel(next ? 'edit' : 'closed');
  const [more, setMore] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState<Deliverable>(deliverable);
  const links = linkFieldsFor(draft.type);
  const versioned = isVersioned(draft.status);
  const typed = draft.version.trim();
  /* What publishing will actually create: whatever staff typed, or the next up. */
  const target = typed && typed !== deliverable.version ? typed : nextVersion(deliverable.version);
  /* Editing a review link or the version number is a new cut — that has to go
     through publish so it lands in version history instead of silently
     overwriting the link a client may already be reviewing. Everything else
     (name, status, visibility) is a plain save. */
  const linkChanged = draft.driveUrl !== deliverable.driveUrl || draft.audioUrl !== deliverable.audioUrl;
  const versionChanged = typed !== deliverable.version.trim();
  const needsPublish = versioned && (linkChanged || versionChanged);

  /**
   * WHAT A TRANSCRIPT BELONGS TO — 29 Sep 2026.
   *
   * Every film and podcast on this project, offered as the source a
   * Transcript item transcribes. This one field is the whole reason a
   * transcript reaches the client page: without it the page has nothing but
   * the two titles to compare, and a transcript that is not named after its
   * source never appears at all — which is exactly what happened to
   * Bloomberg's "Transcript 03", 5,328 words, invisible from the moment it
   * was uploaded. See types/portal.ts → Deliverable.transcriptFor.
   */
  const transcriptSources = activeProject.deliverables.filter(item => item.type === 'Video' || item.type === 'Audio');

  /**
   * One-click Descript transcript generation. No Descript credential can live
   * in the browser, so this never calls Descript itself — it queues a row in
   * transcript_jobs and a scheduled worker (checking roughly hourly) does the
   * actual work. For an Audio deliverable the result lands as a new
   * Transcript deliverable; for a Video deliverable it's attached straight
   * onto this same row instead (10 Sep 2026 — see TranscriptRevisions below).
   * Polled only while a request is outstanding, so an old row a staff member
   * never asked about stays quiet.
   */
  const sourceUrl = transcriptSourceUrl(deliverable);
  const canGenerateTranscript = (deliverable.type === 'Video' || deliverable.type === 'Audio') && Boolean(sourceUrl.trim());
  const [transcriptJob, setTranscriptJob] = useState<TranscriptJob | null>(null);
  const [requestingTranscript, setRequestingTranscript] = useState(false);
  useEffect(() => {
    if (!canGenerateTranscript) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    async function poll() {
      const job = await fetchLatestTranscriptJob(activeProject.id, deliverable.id);
      if (cancelled) return;
      setTranscriptJob(job);
      if (job && (job.status === 'pending' || job.status === 'processing')) {
        /* Once a request has stalled, the worker is not coming back within the
           next twenty seconds — but it may come back, so this backs off to a
           slow check rather than stopping. Polling a dead queue three times a
           minute for the rest of the day helps nobody. */
        timeout = setTimeout(poll, isTranscriptJobStalled(job) ? 300_000 : 20_000);
      }
    }
    void poll();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject.id, deliverable.id, canGenerateTranscript]);
  async function generateTranscript() {
    if (!canGenerateTranscript || requestingTranscript) return;
    setRequestingTranscript(true);
    const result = await requestTranscriptJob({
      projectId: activeProject.id,
      deliverableKey: deliverable.id,
      sourceName: deliverable.name,
      sourceKind: deliverable.type === 'Audio' ? 'audio' : 'video',
      sourceUrl,
      requestedBy: staffUser?.name ?? 'Portal'
    });
    setRequestingTranscript(false);
    setMore(false);
    if (!result.ok) {
      notify(`Transcript not requested · ${result.error}`);
      return;
    }
    notify(`Transcript requested · Descript generates it in the background, usually within the hour`);
    setTranscriptJob({
      id: 'pending-local',
      status: 'pending',
      requestedAt: Date.now()
    });
  }
  function edit(patch: Partial<Deliverable>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save() {
    updateDeliverable(deliverable.id, {
      name: draft.name.trim() || deliverable.name,
      type: draft.type,
      status: draft.status,
      version: versioned ? draft.version : '',
      /* A versioned deliverable's review link only ever changes by publishing
         a new version (see publish, below) — Save never touches it, so it
         can't quietly overwrite what a client is already reviewing. */
      ...(versioned ? {} : {
        driveUrl: draft.driveUrl,
        audioUrl: draft.audioUrl
      }),
      videoFormat: draft.type === 'Video' ? draft.videoFormat ?? '16:9 Landscape' : undefined,
      /* Only a Transcript carries a source. Changing an item's type away from
         Transcript clears it, so a stale pointer cannot linger. */
      transcriptFor: draft.type === 'Transcript' ? draft.transcriptFor ?? '' : '',
      visibleToClient: draft.visibleToClient
      /* A transcript is never carried by Save Changes: it is stored at the
         moment it is uploaded, and read back from the database. */
    });
    notify(`${draft.name || deliverable.name} · changes saved`);
    setOpen(false);
  }
  async function publish() {
    /**
     * The links typed into the form belong to the NEW version, so they are
     * handed to publishVersion rather than saved onto the deliverable first.
     * Saving first would overwrite the outgoing version's stored link and the
     * archived entry would record the new cut's link against the old version.
     *
     * The form only moves on once the database has accepted the publish; a
     * refusal reports itself and leaves everything as it was.
     */
    const published = await publishVersion(deliverable.id, target, {
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl
    });
    if (!published) return;
    edit({
      version: target,
      status: 'Ready for Review'
    });
    setOpen(false);
  }

  /* One button, one decision: if what's changed amounts to a new cut, commit
     publishes it; otherwise commit just saves the details. Staff never has to
     choose between the two themselves. */
  function commit() {
    if (needsPublish) {
      void publish();
    } else {
      save();
    }
  }
  const outstanding = deliverable.changeRequests.filter(request => !request.resolved);
  /** Completed requests stay on the record, newest first. */
  const completed = deliverable.changeRequests.filter(request => request.resolved).reverse();
  const transcriptStatus = transcriptJobLabel(transcriptJob);
  /** Queued long enough that the worker is not coming — see utils/transcriptJobs.ts. */
  const transcriptStalled = isTranscriptJobStalled(transcriptJob);
  /** A Video deliverable's own attached transcript, once one exists (10 Sep
      2026 — see TranscriptRevisions below the Version History). */
  const hasVideoTranscript = deliverable.type === 'Video' && (deliverable.transcriptRevisions?.length ?? 0) > 0;
  /** A stored transcript with nothing to attach it to never reaches a client. */
  const unattachedTranscript = deliverable.type === 'Transcript' && !(deliverable.transcriptFor ?? '').trim();
  return <Reorder.Item value={deliverable} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift(deliverable.id);
  }} onDragEnd={() => {
    setCarrying(false);
    onLift(null);
  }}
  /* The row itself is carried, so it stays fully visible and readable. */ animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-846" data-mp="1gyb9v3y07pwx">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-3 py-6" data-id="element-847" data-mp="3sccrd4ncvnga">
        <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${deliverable.name}`} title="Drag to reorder" className="shrink-0 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-848" data-mp="3dngix6he08f5">
          <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-849" data-mp="55k31h00jrgp" />
        </span>

        {/* The row itself opens the deliverable's details and client feedback. */}
        <button type="button" onClick={() => setPanel(current => current === 'details' ? 'closed' : 'details')} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-5 text-left" data-id="element-850" data-mp="2kz2kipmdza32">
          <StaffTypeMark type={deliverable.type} driveUrl={deliverable.driveUrl} data-id="element-851" data-mp="31c7d12oqhf4l" />
          <span className="min-w-0" data-id="element-852" data-mp="2wd4hc8un8l9a">
            <p className="font-display text-2xl leading-none sm:text-3xl" data-id="element-853" data-mp="2we5uciv7zhtn">{deliverable.name}</p>
            <p className="label-sm mt-3 text-paper/40" data-id="element-854" data-mp="2rfm78f4w3zzp">
              {metaLabel(deliverable)} · {deliverable.status}
              {outstanding.length > 0 ? ` · ${outstanding.length} change${outstanding.length === 1 ? '' : 's'} requested` : ''}
              {transcriptStatus ? ` · ${transcriptStatus}` : ''}
            </p>
          </span>
        </button>

        <button type="button" onClick={() => {
        updateDeliverable(deliverable.id, {
          visibleToClient: !deliverable.visibleToClient
        });
        edit({
          visibleToClient: !deliverable.visibleToClient
        });
      }} aria-pressed={deliverable.visibleToClient} title="Shown in Client Portal" className={['label shrink-0 border px-3 py-2 transition-colors duration-150 ease-out', deliverable.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-855" data-mp="3a3r6paeikysp">
          {deliverable.visibleToClient ? 'Shown' : 'Hidden'}
        </button>

        <div className="relative flex shrink-0 items-center gap-6" data-id="element-856" data-mp="xv101zvalcpl">
          <button type="button" onClick={() => {
          setDraft(deliverable);
          setPanel(current => current === 'edit' ? 'closed' : 'edit');
          setConfirmRemove(false);
          setMore(false);
        }} className={BUTTON_QUIET} data-id="element-857" data-mp="11we72da4fly3">
            {editingRow ? 'Close' : 'Edit'}
          </button>

          {/* Occasional actions, kept off the everyday row. */}
          <button type="button" onClick={() => setMore(current => !current)} aria-expanded={more} aria-label="More options" className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-858" data-mp="3dosyk6kqfs5i">
            More
          </button>

          {more && <div className="absolute right-0 top-full z-40 mt-3 w-56 border border-paper/25 bg-ink" data-id="element-859" data-mp="3aku6jiphtmbe">
              {canGenerateTranscript && <button type="button" disabled={requestingTranscript || transcriptJob?.status === 'processing'} onClick={() => void generateTranscript()} className="label block w-full border-b border-ruleDark px-5 py-4 text-left text-paper/70 transition-colors duration-150 ease-out hover:text-paper disabled:cursor-default disabled:text-paper/30" data-id="element-860" data-mp="3oj1vp4gafjc0">
                  {transcriptStalled ? 'Request Transcript Again' : transcriptJob?.status === 'done' || transcriptJob?.status === 'error' ? 'Regenerate Transcript' : 'Generate Transcript'}
                  <span className="label-sm mt-1 block text-paper/35" data-id="element-861" data-mp="upfn484hquzr">via Descript</span>
                </button>}
              <button type="button" onClick={() => {
            duplicateDeliverable(deliverable.id);
            setMore(false);
            notify(`${deliverable.name} duplicated`);
          }} className="label block w-full border-b border-ruleDark px-5 py-4 text-left text-paper/70 transition-colors duration-150 ease-out hover:text-paper" data-id="element-862" data-mp="lqkgiuxjbivx">
                Duplicate
              </button>
              <button type="button" onClick={() => {
            setConfirmRemove(true);
            setMore(false);
          }} className="label block w-full px-5 py-4 text-left text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-863" data-mp="3k8ppfj95vsde">
                Remove
              </button>
            </div>}
        </div>
      </div>

      {/* A transcript nobody can reach. Said on the row rather than left to be
          discovered by a client who cannot see it — 29 Sep 2026. */}
      {unattachedTranscript && <p className="label-sm mb-6 border-l-2 border-paper/40 px-5 py-3 text-paper/50" data-id="element-864" data-mp="3lqprdbot5cj9">
          This transcript is not attached to anything, so no client can see it. Open Edit and
          choose which film or podcast it belongs to.
        </p>}

      {/* A request the worker has never picked up. Said plainly on the row the
          person who asked for it is looking at, rather than left reading as
          work in progress — see utils/transcriptJobs.ts for why age is the
          only signal available. */}
      {transcriptStalled && <p className="label-sm mb-6 border-l-2 border-paper/40 px-5 py-3 text-paper/50" data-id="element-865" data-mp="2y25c7i7fiu54">
          This transcript request has been queued since{' '}
          {new Date(transcriptJob?.requestedAt ?? Date.now()).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit'
      })}{' '}
          and Descript has not picked it up. Check the scheduled worker is still running, then
          request it again.
        </p>}

      {confirmRemove && <div className="mb-6 border border-paper/30 px-6 py-6" data-id="element-866" data-mp="2a5ilni9kfy99">
          <p className="font-display text-xl leading-snug" data-id="element-867" data-mp="2a3pbhszquvwi">
            Remove {deliverable.name} from the dashboard?
          </p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-868" data-mp="2jwd7iliwgvu6">
            This only removes it from the client dashboard. Nothing is deleted from Google Drive.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6" data-id="element-869" data-mp="1memwkswhvh5p">
            <button type="button" onClick={() => {
          removeDeliverable(deliverable.id);
          notify(`${deliverable.name} removed from dashboard`);
        }} className={BUTTON_SOLID} data-id="element-870" data-mp="1re972ukxujat">
              Remove
            </button>
            <button type="button" onClick={() => setConfirmRemove(false)} className={BUTTON_QUIET} data-id="element-871" data-mp="3tmui1bnhzqmt">
              Cancel
            </button>
          </div>
        </div>}

      {open && <div className="mb-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-872" data-mp="30yyky5u0nwpi">
          {editingRow && <div className="lg:col-span-7" data-id="element-873" data-mp="35udz6yvxrumg">
              {/* The deliverable being worked on leads the panel. */}
              <input value={draft.name} onChange={event => edit({
          name: event.target.value
        })} placeholder="e.g. Event Highlights Film" aria-label="Deliverable title" className="w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-3xl leading-none text-paper placeholder:text-paper/30 focus:border-paper focus:outline-none sm:text-4xl" data-id="element-874" data-mp="39w92g8xfgi2v" />

              <div className="mt-8 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-875" data-mp="29qeyz7h29t6k">
                <label className="block" data-id="element-876" data-mp="25nimzzm10vbl">
                  <span className="label block text-paper/40" data-id="element-877" data-mp="25qelbp1sdm0k">Type</span>
                  <select value={draft.type} onChange={event => edit({
              type: event.target.value as Deliverable['type']
            })} className={SELECT} data-id="element-878" data-mp="20ofhyf61qp3m">
                    {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-879" data-mp="1m1ck27dtaefr">
                        {option}
                      </option>)}
                  </select>
                </label>

                <label className="block" data-id="element-880" data-mp="1cybfib4lgve7">
                  <span className="label block text-paper/40" data-id="element-881" data-mp="1d1h6xx0p49d3">Status</span>
                  {/* The client's wording sits inside the options, not on the page. */}
                  <select value={draft.status} onChange={event => edit({
              status: event.target.value as Deliverable['status']
            })} className={SELECT} data-id="element-882" data-mp="ybcq0lpasb24">
                    {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-883" data-mp="1h3f9s5x8k7gl">
                        {statusOptionLabel(option)}
                      </option>)}
                  </select>
                </label>

                {draft.type === 'Video' && <label className="block" data-id="element-884" data-mp="17bjbatr8mfbu">
                    <span className="label block text-paper/40" data-id="element-885" data-mp="1cc72pgjdoy6l">Video Format</span>
                    <select value={draft.videoFormat ?? '16:9 Landscape'} onChange={event => edit({
              videoFormat: event.target.value as Deliverable['videoFormat']
            })} className={SELECT} data-id="element-886" data-mp="202jh9a94pt65">
                      {VIDEO_FORMATS.map(option => <option key={option} value={option} className={OPTION} data-id="element-887" data-mp="3kihigpov98us">
                          {option}
                        </option>)}
                    </select>
                  </label>}

                {/* Which film or podcast this transcript is for. The client
                    page shows a transcript beside its source and nowhere
                    else, so an unset one is invisible. */}
                {draft.type === 'Transcript' && <label className="block" data-id="element-888" data-mp="3bjneufd4bi7q">
                    <span className="label block text-paper/40" data-id="element-889" data-mp="3bikms44279q8">Transcript For</span>
                    <select value={draft.transcriptFor ?? ''} onChange={event => edit({
              transcriptFor: event.target.value
            })} className={SELECT} data-id="element-890" data-mp="uu8j8gp7wjbu">
                      <option value="" className={OPTION} data-id="element-891" data-mp="1m561cz5azla9">
                        Not attached — no client can see it
                      </option>
                      {transcriptSources.map(item => <option key={item.id} value={item.id} className={OPTION} data-id="element-892" data-mp="1v2yh24exsuy6">
                          {item.name}
                        </option>)}
                    </select>
                  </label>}

                {versioned && <label className="block" data-id="element-893" data-mp="292hlam4mnm57">
                    <span className="label block text-paper/40" data-id="element-894" data-mp="2durau11tyk6y">Version</span>
                    <input value={draft.version} onChange={event => edit({
              version: event.target.value
            })} placeholder="e.g. V3" className={FIELD} data-id="element-895" data-mp="2dwu5wzhub3ij" />
                  </label>}
              </div>

              {draft.type === 'Transcript' && transcriptSources.length === 0 && <p className="label-sm mt-5 text-paper/35" data-id="element-896" data-mp="h2bs7nw01vnd">
                  This project has no film or podcast to attach a transcript to yet.
                </p>}

              {draft.type === 'Transcript' && <TranscriptUpload value={draft.transcript ?? ''} filename={draft.transcriptFilename} wordCount={draft.transcriptWordCount}
        /* Stored immediately, not on Save Changes. */ onUpload={async (text, filename, words) => {
          const result = await uploadTranscript(deliverable.id, text, filename, words);
          if (result.ok) {
            edit({
              transcript: text,
              transcriptFilename: filename,
              transcriptWordCount: words
            });
          }
          return result;
        }} data-id="element-897" data-mp="27xfg5313ih5" />}

              {/* Only the links this type can actually use. */}
              {links.drive && <label className="mt-8 block" data-id="element-898" data-mp="3d3p1hib8kxyd">
                  <span className="label block text-paper/40" data-id="element-899" data-mp="2ybybc0dn2c7i">{links.driveLabel}</span>
                  <input value={draft.driveUrl} onChange={event => edit({
            driveUrl: event.target.value
          })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-900" data-mp="33baw8fky997g" />
                  {draft.driveUrl.trim() && <span className="label-sm mt-3 block text-paper/30" data-id="element-901" data-mp="2fff81rpiniiv">
                      {toDrivePreviewUrl(draft.driveUrl) ? `Embeds as ${toDrivePreviewUrl(draft.driveUrl)}` : isDriveFolder(draft.driveUrl) ? 'Folder link · opens in Drive' : 'Not recognised as a Drive file'}
                    </span>}
                </label>}

              {links.audio && <label className="mt-8 block" data-id="element-902" data-mp="1y648n153jrc9">
                  <span className="label block text-paper/40" data-id="element-903" data-mp="2ctpaysu5dmj4">Audio Review Link</span>
                  <input value={draft.audioUrl} onChange={event => edit({
            audioUrl: event.target.value
          })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-904" data-mp="2cvikqh024mvc" />
                </label>}

              <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-905" data-mp="2gxnah4ndwzcp">
                <span className="label text-paper/40" data-id="element-906" data-mp="2qtgwdemzu575">Shown in Client Portal</span>
                <button type="button" onClick={() => edit({
            visibleToClient: !draft.visibleToClient
          })} aria-pressed={draft.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', draft.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-907" data-mp="2qre0wf32njv1">
                  {draft.visibleToClient ? 'On' : 'Off'}
                </button>
              </label>

              {/* One button that already knows which action it's about to take. */}
              <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-908" data-mp="3srn3je8ynl7e">
                <button type="button" onClick={commit} className={BUTTON_SOLID} data-id="element-909" data-mp="3stpzsg0piakk">
                  {needsPublish ? `Publish ${target} for Review` : 'Save Changes'}
                </button>
              </div>
              <p className="label-sm mt-5 text-paper/30" data-id="element-910" data-mp="1d0r26tnjnqev">
                {needsPublish ? 'Creates a new version and sends it for review — clients see it immediately.' : 'Client sees these updates immediately.'}
              </p>
            </div>}

          <div className={editingRow ? 'lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12' : 'lg:col-span-7'} data-id="element-911" data-mp="31m7mxgl7ijgp">
            {/* Outstanding feedback leads, so nothing needing action is buried. */}
            {outstanding.length > 0 && <>
                <h4 className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-912" data-mp="2itnaf9pteqz2">
                  Requested Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-913" data-mp="2du0wsz62r8pq">
                  {/* Marking one completed is a real, small milestone — it collapses
                      out rather than just vanishing on the next render. */}
                  <AnimatePresence initial={false} data-id="element-914" data-mp="24vp0cgij1gpd">
                    {outstanding.map(request => <motion.li key={request.id} layout exit={{
                opacity: 0,
                height: 0,
                paddingTop: 0,
                paddingBottom: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden border-b border-ruleDark py-5" data-id="element-915" data-mp="812iwzup16or">
                        <span className="label block text-paper" data-id="element-916" data-mp="124gpflhrl0pd">
                          {request.timecode ? `${request.timecode} · ` : ''}
                          {request.authorName}
                        </span>
                        <p className="mt-2 font-display text-lg font-normal leading-relaxed" data-id="element-917" data-mp="afws6rdzoubz">
                          {request.body}
                        </p>
                        <div className="mt-3 flex items-center justify-between gap-4" data-id="element-918" data-mp="jfjwpm20mg99">
                          <span className="label-sm text-paper/35" data-id="element-919" data-mp="ef4fgjem4om0">
                            {request.version ? `${request.version} · ` : ''}
                            Sent {request.time || request.submitted}
                            {waitingLabel(request.at) ? ` · ${waitingLabel(request.at)}` : ''}
                          </span>
                          <button type="button" onClick={() => {
                    resolveChangeRequest(deliverable.id, request.id);
                    notify('Marked as completed');
                  }} className="label-sm shrink-0 text-paper/60 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-920" data-mp="1ydo2t6mv1n32">
                            Mark completed
                          </button>
                        </div>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>
              </>}

            <div className={outstanding.length > 0 ? 'mt-12' : ''} data-id="element-921" data-mp="300xa57cai9m5">
              {deliverable.type === 'Transcript' ? <>
                  <TranscriptRevisions deliverable={deliverable} data-id="element-922" data-mp="34sf9xs2ku45j" />
                  <TranscriptComments deliverable={deliverable} data-id="element-923" data-mp="3endvldyonbfr" />
                </> : <>
                  <VersionHistory deliverable={deliverable} data-id="element-924" data-mp="39pkpekxdqhfh" />
                  {/* A video's auto-generated transcript is attached
                      directly to it now (10 Sep 2026) rather than living as
                      its own Transcript deliverable — staff can still see
                      every saved state and restore an earlier one if
                      Descript is ever re-run. No TranscriptComments here:
                      clients only ever see this transcript read-only, so
                      there is never anything to show. */}
                  {hasVideoTranscript && <TranscriptRevisions deliverable={deliverable} data-id="element-925" data-mp="2h0n4sb5p48jk" />}
                </>}
            </div>

            {completed.length > 0 && <>
                <h4 className="label mt-12 border-b border-ruleDark pb-4 text-paper/40" data-id="element-926" data-mp="27zfjctoa326w">
                  Completed Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-927" data-mp="ngd7pz4hfuj5">
                  {completed.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-928" data-mp="seoe1zvkjmwm">
                      <span className="label block text-paper/60" data-id="element-929" data-mp="wiw729927p0n">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg font-normal leading-relaxed text-paper/60" data-id="element-930" data-mp="4s87v2qy4tpm">
                        {request.body}
                      </p>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-931" data-mp="dr3a63qvdjnn">
                        {request.version ? `${request.version} · ` : ''}
                        Requested {request.time || request.submitted}
                        {request.completedAt ? ` · Completed ${request.completedAt}` : ' · Completed'}
                        {request.completedBy ? ` by ${request.completedBy}` : ''}
                      </span>
                    </li>)}
                </ul>
              </>}
          </div>
        </div>}
    </Reorder.Item>;
}